import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import {
    IconButton,
    Box,
    FormControlLabel,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Popover,
    Checkbox,
    Autocomplete,
    TextField,
    CircularProgress,
    Popper,
} from '@mui/material';
import {
    KeyboardArrowDown as KeyboardArrowDownIcon,
    KeyboardArrowUp as KeyboardArrowUpIcon,
} from '@mui/icons-material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import documentGridStyles from '../../Document/SubComponents/DocumentDataGrid.module.scss';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enUS, arSA, he } from 'date-fns/locale';
import NoDocumentScanned from '../../../assests/images/svgs/NoDocumentScanned.svg';
import { beautifyNumber } from '../../../Utils/FormatNumber';
import { useTranslation } from 'react-i18next';
import editIcon from '../../../assests/images/Icons/editIcon.svg';
import Constants from '../../../assests/Constants/constants';
import {
    getGridStringOperatorsWithComparison,
    getGridNumericOperatorsWithNotLike,
    getGridDateOperatorsWithNotLike,
} from '../../../Utils/DataGridFilterOperators';
import PaymentAcceptanceExpandPanel from './PaymentAcceptanceExpandPanel';

const StyledProjectsPopper = styled(Popper)({
    '& .MuiPaper-root': {
        borderRadius: '8px',
        background: '#ffffff',
        border: '1px solid #e5e7eb',
        boxShadow: '0 12px 28px rgba(20, 30, 43, 0.16)',
        overflow: 'hidden',
    },
    '& .MuiAutocomplete-listbox': {
        padding: 0,
        textAlign: 'start',
    },
    '& .MuiAutocomplete-option': {
        justifyContent: 'flex-start',
        textAlign: 'start',
        fontSize: 'var(--xtram-text-sm, 13px)',
        fontWeight: 500,
        minHeight: 'var(--xtram-menu-item-height, 36px)',
        '&[aria-selected="true"]': {
            background: 'rgba(48, 79, 255, 0.1) !important',
            color: '#304fff',
            fontWeight: 700,
        },
        '&.Mui-focused, &:hover': {
            background: '#304fff !important',
            color: '#ffffff',
        },
    },
});

const formatDateMonth = (dateString) => {
    const date = new Date(dateString);
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const year = date.getFullYear();
    return `${month}-${year}`;
};

const formatIsoDate = (value) => {
    if (!value) return '-';
    try {
        return new Date(value).toISOString().split('T')[0];
    } catch {
        return '-';
    }
};

const DETAIL_ID_SUFFIX = '__detail';

const hasExpandableContent = (row) =>
    Boolean(
        (row?.checks && row.checks.length) ||
        (row?.creditCards && row.creditCards.length) ||
        (row?.cash && row.cash.length) ||
        (row?.bankTransactions && row.bankTransactions.length) ||
        (row?.offsets && row.offsets.length)
    );

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
    width: '100%',
    '& .MuiAutocomplete-root': {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    '& *': {
        boxSizing: 'border-box !important',
    },
    '& .MuiDataGrid-columnHeaders': {
        backgroundColor: '#E3E7FE ',
        minHeight: '42px !important',
        maxHeight: '42px !important',
        lineHeight: '42px',
    },
    '& .MuiDataGrid-columnHeader': {
        backgroundColor: '#E3E7FE ',
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 8px',
        minHeight: '42px !important',
        maxHeight: '42px !important',
        height: '42px !important',
        boxSizing: 'border-box',
    },
    '& .MuiDataGrid-columnHeaderTitle': {
        color: '#1222b3',
        fontFamily: 'Montserrat',
        fontSize: '13px',
        fontWeight: 600,
        lineHeight: 'normal',
    },
    '& .MuiDataGrid-sortIcon': {
        opacity: '0.8 !important',
        color: '#1222b3',
    },
    '& .MuiDataGrid-menuIconButton': {
        color: '#1222b3',
    },
    // Override MUI default row-reverse for right-aligned headers (RTL)
    '& .MuiDataGrid-columnHeader--alignRight .MuiDataGrid-columnHeaderDraggableContainer, & .MuiDataGrid-columnHeader--alignRight .MuiDataGrid-columnHeaderTitleContainer': {
        flexDirection: 'row',
    },
    // Override MUI default flex-end for right-aligned cells (RTL)
    '& .MuiDataGrid-cell--textRight': {
        textAlign: 'left',
        justifyContent: 'flex-start',
    },
    '& .MuiDataGrid-virtualScroller': {
        scrollbarColor: '#d8daf2 transparent',
        borderRadius: 'inherit',
    },
    '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
        width: '6px',
        height: '6px',
    },
    '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track': {
        background: 'transparent',
    },
    '& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb': {
        background: '#d8daf2',
        borderRadius: 'var(--xtram-radius-pill, 999px)',
    },
    '& .MuiDataGrid-scrollbar': {
        scrollbarWidth: 'var(--register-scrollbar-width, var(--xtram-scrollbar-width, thin))',
        scrollbarColor: '#d8daf2 transparent',
        borderRadius: 'inherit',
    },
    '& .MuiDataGrid-scrollbar::-webkit-scrollbar': {
        width: '6px',
        height: '6px',
    },
    '& .MuiDataGrid-scrollbar::-webkit-scrollbar-track': {
        background: 'transparent',
    },
    '& .MuiDataGrid-scrollbar::-webkit-scrollbar-thumb': {
        background: '#d8daf2',
        borderRadius: 'var(--xtram-radius-pill, 999px)',
    },
    '& .MuiDataGrid-cell': {
        borderTop: '1px solid rgba(191, 191, 193, 0.56)',
        backgroundColor: 'rgba(255, 255, 255, 0.32)',
        whiteSpace: 'normal',
        wordWrap: 'break-word',
        display: 'flex',
        alignItems: 'center',
        fontSize: 'var(--xtram-grid-cell-font, 13px)',
    },
    '& .MuiDataGrid-cell[data-field="expand"]': {
        justifyContent: 'center',
        alignItems: 'center',
        padding: 0,
    },
    '& .MuiDataGrid-row:not(.MuiDataGrid-row--detail) .MuiDataGrid-cell[data-field="expand"] > *': {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    '& .MuiDataGrid-row--detail .MuiDataGrid-cell[data-field="expand"]': {
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
    },
    '& .MuiDataGrid-cell[data-field="document"]': {
        position: 'relative',
        display: 'flex !important',
        alignItems: 'center !important',
        justifyContent: 'center !important',
        overflow: 'hidden',
        padding: '0 !important',
    },
    '& .MuiDataGrid-cell[data-field="document"] .pa-document-preview': {
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    '& .MuiDataGrid-cell[data-field="document"] .iframe-container': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 0,
        margin: 0,
        height: '50px',
    },
    '& .MuiDataGrid-cell[data-field="document"] .iframe-container iframe, & .MuiDataGrid-cell[data-field="document"] .zoom-iframe, & .MuiDataGrid-cell[data-field="document"] img': {
        display: 'block',
        width: '100px',
        height: '50px',
        margin: 0,
        border: 0,
        verticalAlign: 'middle',
    },
    '& .MuiDataGrid-columnHeader[data-field="actions"], & .MuiDataGrid-cell[data-field="actions"]': {
        maxWidth: '48px !important',
        minWidth: '48px !important',
        width: '48px !important',
        padding: '0 !important',
        justifyContent: 'center',
        alignItems: 'center',
    },
    '& .MuiDataGrid-row:not(.MuiDataGrid-row--detail) .MuiDataGrid-cell[data-field="actions"] > *': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        height: '100%',
    },
    '& .MuiDataGrid-columnHeader[data-field="actions"] .MuiDataGrid-columnSeparator': {
        display: 'none',
    },
    '& .MuiDataGrid-row:hover': {
        backgroundColor: 'rgba(226, 231, 252, 0.3)',
    },
    '& .MuiDataGrid-cell:focus': {
        outline: 'none',
    },
    '& .MuiDataGrid-row--detail': {
        backgroundColor: '#fafbff',
    },
    '& .MuiDataGrid-row--detail .MuiDataGrid-cellCheckbox': {
        display: 'none',
    },
    '& .MuiDataGrid-row--detail .MuiDataGrid-cell': {
        alignItems: 'flex-start',
        maxHeight: 'none !important',
        overflow: 'visible !important',
        whiteSpace: 'normal',
        paddingTop: '8px',
        paddingBottom: '8px',
    },
    '& .duplicate-from-cell, & .duplicate-from-desc': {
        color: '#ffffff',
        fontWeight: 600,
    },
    '& .flickering-cell, & .duplicate-desc': {
        color: '#ffffff',
        fontWeight: 600,
    },
}));

const STORAGE_KEY_PA_VIEW = 'paymentAcceptanceTable_view';
const STORAGE_KEY_PA_COLUMN_VISIBILITY = 'paymentAcceptanceTable_columnVisibility';
const STORAGE_KEY_PA_COLUMN_VISIBILITY_CUSTOM = 'paymentAcceptanceTable_columnVisibility_custom';
const TABLE_VIEWS = ['compact', 'standard', 'full', 'custom'];
// Compact = essentials that fit typical viewport; standard/full add wider secondary columns.
const VIEW_INCLUDES_TIER = {
    compact: ['priority', 'compact'],
    standard: ['priority', 'compact', 'standard'],
    full: ['priority', 'compact', 'standard', 'full'],
    custom: ['priority', 'compact', 'standard', 'full'],
};
const DEFAULT_HIDDEN_COLUMNS = {};

const TABLE_VIEW_MENU_PROPS = {
    PaperProps: {
        sx: {
            mt: 0.5,
            borderRadius: '8px',
            border: '1px solid #e5e7f6',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden',
            minWidth: 'var(--xtram-menu-width)',
            '& .MuiList-root': { p: 0 },
            '& .MuiMenuItem-root': {
                minHeight: 'var(--xtram-menu-item-height)',
                px: 'var(--xtram-menu-item-pad-x)',
                py: 'var(--xtram-menu-item-pad-y)',
                fontSize: 'var(--xtram-text-sm)',
                fontWeight: 400,
                lineHeight: 1.35,
                color: '#696665',
                transition: 'background-color 0.2s ease, color 0.2s ease',
                '&:not(:last-of-type)': { borderBottom: '1px solid #f0f0f0' },
                '&:hover': { backgroundColor: '#304fff', color: '#ffffff' },
                '&.Mui-selected': {
                    backgroundColor: 'rgba(48, 79, 255, 0.1)',
                    color: '#304fff',
                    fontWeight: 500,
                },
                '&.Mui-selected:hover': { backgroundColor: '#304fff', color: '#ffffff' },
            },
        },
    },
};

const COLUMN_PANEL_PAPER_SX = {
    p: 0,
    mt: 0.5,
    maxHeight: 360,
    width: 'var(--xtram-menu-width)',
    minWidth: 'var(--xtram-menu-width)',
    maxWidth: 'var(--xtram-menu-width)',
    borderRadius: '8px',
    border: '1px solid #e5e7f6',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    overflow: 'hidden',
};

const PaymentAcceptanceTable = ({
    filteredAndSortedData,
    subAccountantOptions,
    client,
    setIsAllSelected,
    handleEdit,
    openPdfInNewTab,
    accountCardMap,
    isRange,
    direction,
    handleSelectRows,
    selectedRows,
    handleRightClickAction,
    userTypeContext,
    permissionsState,
    hideStatus = false,
    handleDownloadDocument,
    byProjectsFilterActive = false,
    onByProjectsFilterToggle = () => {},
    loadingSegmentations = false,
    projectAutocompleteOptions = [],
    selectedFromSegmentation = null,
    selectedToSegmentation = null,
    onFromSegmentationChange = () => {},
    onToSegmentationChange = () => {},
}) => {
    const { i18n, t } = useTranslation();
    const getAlignment = () => (i18n.language === 'en' ? 'left' : 'right');

    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 100 });
    const [sortModel, setSortModel] = useState([]);
    const [openRows, setOpenRows] = useState({});

    const [tableView, setTableView] = useState(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY_PA_VIEW);
            return TABLE_VIEWS.includes(saved) ? saved : 'standard';
        } catch {
            return 'standard';
        }
    });
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY_PA_VIEW, tableView);
        } catch (_) {}
    }, [tableView]);

    const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
        try {
            const savedView = localStorage.getItem(STORAGE_KEY_PA_VIEW);
            if (savedView === 'custom') {
                const rawCustom = localStorage.getItem(STORAGE_KEY_PA_COLUMN_VISIBILITY_CUSTOM);
                if (rawCustom) {
                    const parsed = JSON.parse(rawCustom);
                    if (typeof parsed === 'object' && parsed !== null) return { ...DEFAULT_HIDDEN_COLUMNS, ...parsed };
                }
            }
            const raw = localStorage.getItem(STORAGE_KEY_PA_COLUMN_VISIBILITY);
            if (!raw) return { ...DEFAULT_HIDDEN_COLUMNS };
            const parsed = JSON.parse(raw);
            const saved = typeof parsed === 'object' && parsed !== null ? parsed : {};
            return { ...DEFAULT_HIDDEN_COLUMNS, ...saved };
        } catch {
            return { ...DEFAULT_HIDDEN_COLUMNS };
        }
    });
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY_PA_COLUMN_VISIBILITY, JSON.stringify(columnVisibilityModel));
            if (tableView === 'custom') {
                localStorage.setItem(STORAGE_KEY_PA_COLUMN_VISIBILITY_CUSTOM, JSON.stringify(columnVisibilityModel));
            }
        } catch (_) {}
    }, [columnVisibilityModel, tableView]);

    const [columnsPanelAnchor, setColumnsPanelAnchor] = useState(null);

    const stringFilterOperators = useMemo(() => getGridStringOperatorsWithComparison(t), [t]);
    const numericFilterOperators = useMemo(() => getGridNumericOperatorsWithNotLike(t), [t]);
    const dateFilterOperators = useMemo(() => getGridDateOperatorsWithNotLike(t), [t]);
    const visibleColCountRef = React.useRef(1);

    const getLocale = () => {
        switch (i18n.language) {
            case 'ar':
                return arSA;
            case 'he':
                return he;
            default:
                return enUS;
        }
    };

    const handleToggleRow = useCallback((rowId) => {
        setOpenRows((prev) => ({
            ...prev,
            [rowId]: !prev[rowId],
        }));
    }, []);

    const isCheckboxSelectionEnabled =
        userTypeContext?.toString() !== Constants.UserType.Client || permissionsState?.Premium;

    useEffect(() => {
        if (selectedRows && selectedRows.length > 0 && filteredAndSortedData.length > 0) {
            setIsAllSelected?.(selectedRows.length === filteredAndSortedData.length);
        } else {
            setIsAllSelected?.(false);
        }
    }, [selectedRows, filteredAndSortedData, setIsAllSelected]);

    useEffect(() => {
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
        setOpenRows({});
    }, [filteredAndSortedData]);

    const handleSelectionModelChange = (ids) => {
        const filteredIds = (ids || []).filter((id) => !String(id).endsWith(DETAIL_ID_SUFFIX));
        handleSelectRows(filteredIds);
    };

    const handleCellDoubleClick = (params) => {
        if (params?.row?.isDetail) return;
        // Expand / actions controls must not open the edit modal
        if (params?.field === 'expand' || params?.field === 'actions') return;
        handleEdit?.(params.row);
    };

    const isRowSelectable = (params) => {
        if (params?.row?.isDetail) return false;
        if (!isCheckboxSelectionEnabled) return false;
        return true;
    };

    const compareValues = (a, b, field) => {
        const va = a?.[field];
        const vb = b?.[field];
        if (va == null && vb == null) return 0;
        if (va == null) return -1;
        if (vb == null) return 1;
        if (typeof va === 'number' && typeof vb === 'number') return va - vb;
        const da = new Date(va);
        const db = new Date(vb);
        if (!Number.isNaN(da.getTime()) && !Number.isNaN(db.getTime()) && String(va).match(/\d{4}/)) {
            return da - db;
        }
        return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    };

    const gridRows = useMemo(() => {
        let data = Array.isArray(filteredAndSortedData) ? [...filteredAndSortedData] : [];
        if (sortModel?.length > 0) {
            const { field, sort } = sortModel[0];
            const dir = sort === 'desc' ? -1 : 1;
            data.sort((a, b) => dir * compareValues(a, b, field));
        }
        const result = [];
        for (const row of data) {
            result.push(row);
            if (openRows[row.id] && hasExpandableContent(row)) {
                result.push({
                    id: `${row.id}${DETAIL_ID_SUFFIX}`,
                    isDetail: true,
                    parentRow: row,
                });
            }
        }
        return result;
    }, [filteredAndSortedData, openRows, sortModel]);

    const baseColumns = useMemo(() => {
        const align = getAlignment();

        return [
            {
                field: 'expand',
                headerName: '',
                width: 46,
                minWidth: 46,
                align: 'center',
                headerAlign: 'center',
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                hideable: false,
                viewTier: 'priority',
                colSpan: (_value, row) => {
                    if (!row?.isDetail) return undefined;
                    // Span all currently rendered columns (expand + data cols in this array)
                    return Math.max(visibleColCountRef.current || 1, 1);
                },
                renderCell: (params) => {
                    if (params.row.isDetail) {
                        return (
                            <Box sx={{ width: '100%', py: 0.5 }}>
                                <PaymentAcceptanceExpandPanel
                                    row={params.row.parentRow}
                    t={t}
                                    accountCardMap={accountCardMap}
                                    openPdfInNewTab={openPdfInNewTab}
                    handleDownloadDocument={handleDownloadDocument}
                                    getAlignment={getAlignment}
                                />
                            </Box>
                        );
                    }

                if (!hasExpandableContent(params.row)) return null;

                const open = !!openRows[params.row.id];
    return (
                    <Box
                                            sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                            <IconButton
                                aria-label="expand row"
                                size="small"
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    handleToggleRow(params.row.id);
                                }}
                                onDoubleClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                }}
                                sx={{ padding: '4px' }}
                            >
                                {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                            </IconButton>
                        </Box>
                    );
                },
            },
            {
                field: 'document',
                headerName: t('Document'),
                headerAlign: 'center',
                align: 'center',
                flex: 1.1,
                minWidth: 100,
                sortable: false,
                filterable: false,
                viewTier: 'priority',
                renderCell: (params) => {
                    if (params.row.isDetail) return null;
                    const url = params.row.receiptDocumentUrl;
                    return (
                        <div className="pa-document-preview">
                            {url !== null && url !== undefined ? (
                                <div
                                    className="iframe-container"
                                    onClick={() => openPdfInNewTab(url)}
                                    style={{ cursor: 'pointer' }}
                                >
                                <iframe
                                    className="zoom-iframe"
                                        src={url}
                                    width="100px"
                                    height="50px"
                                        id={params.row.id}
                                        title={params.row.title || 'Document'}
                                        style={{ pointerEvents: 'none', display: 'block' }}
                                />
                            </div>
                        ) : (
                            <img
                                src={NoDocumentScanned}
                                alt="No Document Scanned"
                                width="100px"
                                height="50px"
                                    onClick={() => openPdfInNewTab(url)}
                                    style={{ cursor: 'pointer', display: 'block' }}
                                />
                            )}
                        </div>
                    );
                },
            },
            {
                field: 'processed',
                headerName: t('DocumentProcessed'),
                headerAlign: align,
                align,
                flex: 0.7,
                minWidth: 85,
                viewTier: 'standard',
                statusOnly: true,
                filterOperators: stringFilterOperators,
                valueGetter: (_value, row) => {
                    if (row?.isDetail) return '';
                    const isIdInChecks = client?.documentIds?.checks?.includes(row.id) ?? false;
                    return isIdInChecks ? t('unprocessed') : t('processed');
                },
                renderCell: (params) => {
                    if (params.row.isDetail) return null;
                    const isIdInChecks = client?.documentIds?.checks?.includes(params.row.id) ?? false;
                    return (
                        <p
                            className={isIdInChecks ? 'unprocessed' : 'processed'}
                            onContextMenu={(event) => {
                                event.preventDefault();
                                const status = isIdInChecks ? 'done' : 'notDone';
                                handleRightClickAction?.(params.row.id, status, params.row);
                            }}
                                    style={{
                                        cursor: 'context-menu',
                                fontSize: 'var(--xtram-grid-cell-font, 13px)',
                                margin: 0,
                                        padding: '2px 6px',
                                backgroundColor: isIdInChecks ? '#fff3e0' : '#e8f5e8',
                                color: isIdInChecks ? '#f57c00' : '#2e7d32',
                                fontWeight: 600,
                                        textAlign: 'center',
                                        minWidth: '60px',
                                    }}
                                >
                            {isIdInChecks ? t('unprocessed') : t('processed')}
                        </p>
                    );
                },
            },
            {
                field: 'isLocked',
                headerName: t('Status'),
                headerAlign: align,
                align,
                flex: 0.7,
                minWidth: 85,
                viewTier: 'standard',
                statusOnly: true,
                filterOperators: stringFilterOperators,
                valueGetter: (_value, row) => {
                    if (row?.isDetail) return '';
                    return row?.isLocked ? t('Permanent') : t('Temporary');
                },
                renderCell: (params) => {
                    if (params.row.isDetail) return null;
                    const isLocked = params.row.isLocked;
                    return (
                        <div
                            className={isLocked ? 'status-permanent' : 'status-temporary'}
                                style={{
                                fontSize: 'var(--xtram-grid-cell-font, 13px)',
                                margin: 0,
                                    padding: '2px 6px',
                                fontWeight: 600,
                                    textAlign: 'center',
                                    minWidth: '60px',
                                }}
                            >
                                {isLocked ? t('Permanent') : t('Temporary')}
                            </div>
                    );
                },
            },
            {
                field: 'processedBy',
                headerName: t('DocumentProcessedBy'),
                headerAlign: align,
                align,
                flex: 1,
                minWidth: 100,
                viewTier: 'full',
                statusOnly: true,
                filterOperators: stringFilterOperators,
                valueGetter: (_value, row) => {
                    if (row?.isDetail) return '';
                    if (!row?.processedBy) return '-';
                    return subAccountantOptions?.find((option) => option.value === row.processedBy)?.label || '-';
                },
                renderCell: (params) => {
                    if (params.row.isDetail) return null;
                    if (!params.row.processedBy) return '-';
                    return (
                        subAccountantOptions?.find((option) => option.value === params.row.processedBy)?.label || '-'
                    );
                },
            },
            {
                field: 'createdBy',
                headerName: t('DocumentCreatedBy'),
                headerAlign: align,
                align,
                flex: 1,
                minWidth: 100,
                viewTier: 'full',
                statusOnly: true,
                filterOperators: stringFilterOperators,
                valueGetter: (_value, row) => {
                    if (row?.isDetail) return '';
                    if (!(row?.createdByUserId || row?.createdByUserType)) return '';
                    const found = subAccountantOptions?.find((option) => option.value === row.createdByUserId);
                    if (found?.label) return found.label;
                    if (row.createdByUserType === 'Client') return t('Client');
                    return `${row.createdByUserId || ''}${row.createdByUserType ? ` (${row.createdByUserType})` : ''}`.trim();
                },
                renderCell: (params) => {
                    if (params.row.isDetail) return null;
                    const row = params.row;
                    if (!(row.createdByUserId || row.createdByUserType)) return '—';
                    const found = subAccountantOptions?.find((option) => option.value === row.createdByUserId);
                    if (found?.label) return found.label;
                    if (row.createdByUserType === 'Client') return t('Client');
                    return (
                        `${row.createdByUserId || ''}${row.createdByUserType ? ` (${row.createdByUserType})` : ''}`.trim() ||
                        '—'
                    );
                },
            },
            {
                field: 'description',
                headerName: t('Description'),
                headerAlign: align,
                align,
                flex: 1.2,
                minWidth: 120,
                viewTier: 'priority',
                filterOperators: stringFilterOperators,
                renderCell: (params) => {
                    if (params.row.isDetail) return null;
                    const descriptionText = params.row?.description || '';
                            const isDuplicateFrom = descriptionText.includes('שוכפל מ');
                    const isDuplicate =
                        descriptionText.includes('כפולה ל מסמך שמספרו') ||
                        descriptionText.includes('מסמך כפול למספר');
                    const cellClass = isDuplicateFrom
                        ? 'duplicate-from-cell'
                        : isDuplicate
                          ? 'flickering-cell'
                          : '';
                            return (
                                <div
                                    className={`description-cell ${cellClass}`}
                                    title={descriptionText}
                                    style={{
                                        fontSize: 'var(--xtram-grid-header-font, 13px)',
                                        textAlign: 'right',
                                        maxWidth: '200px',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        color: isDuplicate || isDuplicateFrom ? '#ffffff' : undefined,
                                    }}
                                >
                                    {descriptionText}
                                </div>
                            );
                },
            },
            {
                field: 'reference',
                headerName: t('Reference'),
                headerAlign: align,
                align,
                flex: 0.9,
                minWidth: 90,
                viewTier: 'standard',
                filterOperators: stringFilterOperators,
            },
            {
                field: 'withholdingTax',
                headerName: t('WithholdingTax'),
                headerAlign: align,
                align,
                flex: 0.9,
                minWidth: 90,
                type: 'number',
                viewTier: 'standard',
                filterOperators: numericFilterOperators,
                renderCell: (params) => {
                    if (params.row.isDetail) return null;
                    return (
                        <p
                            style={{
                                color: 'green',
                                fontSize: 'var(--xtram-panel-kpi-value, 16px)',
                                fontWeight: 'bold',
                                margin: 0,
                            }}
                        >
                            {params.row?.withholdingTax?.toLocaleString?.() ?? params.row?.withholdingTax}
                        </p>
                    );
                },
            },
            {
                field: 'amount',
                headerName: t('Amount'),
                headerAlign: align,
                align,
                flex: 1,
                minWidth: 90,
                type: 'number',
                viewTier: 'priority',
                filterOperators: numericFilterOperators,
                renderCell: (params) => {
                    if (params.row.isDetail) return null;
                    return (
                        <p
                                                                                    style={{ 
                                color: 'green',
                                fontSize: 'var(--xtram-panel-kpi-value, 16px)',
                                fontWeight: 'bold',
                                margin: 0,
                            }}
                        >
                            {beautifyNumber(params.row?.amount)}
                        </p>
                    );
                },
            },
            {
                field: 'paymentAcceptanceType',
                headerName: t('DocumentType'),
                headerAlign: align,
                align,
                flex: 0.8,
                minWidth: 90,
                viewTier: 'compact',
                filterOperators: stringFilterOperators,
            },
            {
                field: 'debitCreditAccountAmount',
                headerName: t('DebitCreditAccountAmount'),
                headerAlign: align,
                align,
                flex: 1,
                minWidth: 100,
                type: 'number',
                viewTier: 'standard',
                filterOperators: numericFilterOperators,
                renderCell: (params) => {
                    if (params.row.isDetail) return null;
                    return (
                        <p
                                                                                style={{ 
                                color: 'green',
                                fontSize: 'var(--xtram-panel-kpi-value, 16px)',
                                fontWeight: 'bold',
                                textAlign: 'center',
                                margin: 0,
                            }}
                        >
                            {beautifyNumber(params.row?.debitCreditAccountAmount)}
                        </p>
                    );
                },
            },
            {
                field: 'accountCodeName',
                headerName: t('CheckTitle'),
                headerAlign: align,
                align,
                flex: 1.1,
                minWidth: 110,
                viewTier: 'compact',
                filterOperators: stringFilterOperators,
                valueGetter: (_value, row) => {
                    if (row?.isDetail) return '';
                    return accountCardMap?.[row.accountCodeId] || row.accountCodeName || '';
                },
                renderCell: (params) => {
                    if (params.row.isDetail) return null;
                    return accountCardMap?.[params.row.accountCodeId] || params.row.accountCodeName;
                },
            },
            {
                field: 'uploadDate',
                headerName: t('Upload Date'),
                headerAlign: align,
                align,
                flex: 0.9,
                minWidth: 100,
                type: 'date',
                viewTier: 'full',
                filterOperators: dateFilterOperators,
                valueGetter: (value) => {
                    if (!value) return null;
                    const d = new Date(value);
                    return Number.isNaN(d.getTime()) ? null : d;
                },
                valueFormatter: (value) => formatIsoDate(value),
                renderCell: (params) => {
                    if (params.row.isDetail) return null;
                    return formatIsoDate(params.row?.uploadDate);
                },
            },
            {
                field: 'documentDate',
                headerName: t('Document Date'),
                headerAlign: align,
                align,
                flex: 0.9,
                minWidth: 100,
                type: 'date',
                viewTier: 'compact',
                filterOperators: dateFilterOperators,
                valueGetter: (value) => {
                    if (!value) return null;
                    const d = new Date(value);
                    return Number.isNaN(d.getTime()) ? null : d;
                },
                valueFormatter: (value) => formatIsoDate(value),
                renderCell: (params) => {
                    if (params.row.isDetail) return null;
                    return params.row?.documentDate ? formatIsoDate(params.row.documentDate) : '-';
                },
            },
            {
                field: 'uploadNumber',
                headerName: t('UploadNumber'),
                headerAlign: align,
                align,
                flex: 0.7,
                minWidth: 80,
                type: 'number',
                viewTier: 'standard',
                filterOperators: numericFilterOperators,
            },
            {
                field: 'reportingMonthDate',
                headerName: t('MonthReportDate'),
                headerAlign: align,
                align,
                flex: 0.9,
                minWidth: 100,
                viewTier: 'full',
                rangeOnly: true,
                filterOperators: dateFilterOperators,
                valueGetter: (value) => {
                    if (!value) return null;
                    const d = new Date(value);
                    return Number.isNaN(d.getTime()) ? null : d;
                },
                renderCell: (params) => {
                    if (params.row.isDetail) return null;
                    return params.row.reportingMonthDate
                        ? formatDateMonth(params.row.reportingMonthDate)
                        : '-';
                },
            },
            {
                field: 'registrationMonthDate',
                headerName: t('RegistrationMonthDate'),
                headerAlign: align,
                align,
                flex: 0.9,
                minWidth: 100,
                viewTier: 'full',
                rangeOnly: true,
                filterOperators: dateFilterOperators,
                valueGetter: (value) => {
                    if (!value) return null;
                    const d = new Date(value);
                    return Number.isNaN(d.getTime()) ? null : d;
                },
                renderCell: (params) => {
                    if (params.row.isDetail) return null;
                    return params.row.registrationMonthDate
                        ? formatDateMonth(params.row.registrationMonthDate)
                        : '-';
                },
            },
            {
                field: 'actions',
                headerName: '',
                headerAlign: 'center',
                align: 'center',
                width: 48,
                minWidth: 48,
                maxWidth: 48,
                flex: 0,
                resizable: false,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                hideable: false,
                viewTier: 'priority',
                renderCell: (params) => {
                    if (params.row.isDetail) return null;
                    return (
                        <Box
                            sx={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <button
                                type="button"
                                className="row-buttons"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                    handleEdit?.(params.row);
                                }}
                                onDoubleClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                }}
                            >
                                <img src={editIcon} alt="Edit" />
                            </button>
                                        </Box>
                    );
                },
            },
        ];
    }, [
        t,
        i18n.language,
        stringFilterOperators,
        numericFilterOperators,
        dateFilterOperators,
        client,
        subAccountantOptions,
        accountCardMap,
        openPdfInNewTab,
        handleDownloadDocument,
        handleRightClickAction,
        handleEdit,
        openRows,
        handleToggleRow,
    ]);

    const visibleColumns = useMemo(() => {
        const included = VIEW_INCLUDES_TIER[tableView] || VIEW_INCLUDES_TIER.standard;
        return baseColumns
            .filter((col) => included.includes(col.viewTier))
            .filter((col) => (hideStatus ? !col.statusOnly : true))
            .filter((col) => (isRange ? true : !col.rangeOnly))
            .map(({ viewTier, statusOnly, rangeOnly, ...col }) => col);
    }, [baseColumns, tableView, hideStatus, isRange]);

    visibleColCountRef.current = visibleColumns.filter(
        (c) => columnVisibilityModel[c.field] !== false
    ).length;

    const handleColumnVisibilityModelChange = (newModel) => {
        setColumnVisibilityModel({ ...DEFAULT_HIDDEN_COLUMNS, ...newModel });
    };

    const getRowHeight = (params) => {
        if (params?.model?.isDetail || params?.id?.toString?.().endsWith(DETAIL_ID_SUFFIX)) {
            return 'auto';
        }
        return 64;
    };

    const getRowClassName = (params) => (params.row?.isDetail ? 'MuiDataGrid-row--detail' : '');

    return (
        <div className={`${direction}`} style={{ width: '100%', height: '55vh' }}>
            <Box
                className={`${documentGridStyles.toolbar} ${direction === 'rtl' ? documentGridStyles.rtl : documentGridStyles.ltr}`}
                dir={direction}
            >
                <div className={documentGridStyles.tableViewGroup}>
                    <FormControl size="small" className={documentGridStyles.tableViewControl}>
                        <InputLabel id="payment-acceptance-view-label">{t('TableView')}</InputLabel>
                        <Select
                            labelId="payment-acceptance-view-label"
                            value={tableView}
                            label={t('TableView')}
                            dir={direction}
                            MenuProps={TABLE_VIEW_MENU_PROPS}
                            onChange={(e) => {
                                const newView = e.target.value;
                                if (newView !== 'custom') {
                                    try {
                                        localStorage.setItem(
                                            STORAGE_KEY_PA_COLUMN_VISIBILITY_CUSTOM,
                                            JSON.stringify(columnVisibilityModel)
                                        );
                                    } catch (_) {}
                                    setColumnVisibilityModel({ ...DEFAULT_HIDDEN_COLUMNS });
                                } else {
                                    try {
                                        const raw = localStorage.getItem(STORAGE_KEY_PA_COLUMN_VISIBILITY_CUSTOM);
                                        if (raw) {
                                            const parsed = JSON.parse(raw);
                                            if (typeof parsed === 'object' && parsed !== null) {
                                                setColumnVisibilityModel({ ...DEFAULT_HIDDEN_COLUMNS, ...parsed });
                                            }
                                        }
                                    } catch (_) {}
                                }
                                setTableView(newView);
                            }}
                            sx={{
                                height: 'var(--xtram-control-height, 40px)',
                                minHeight: 'var(--xtram-control-height, 40px)',
                                '& .MuiSelect-select': {
                                    textAlign: direction === 'rtl' ? 'left' : 'right',
                                    py: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                },
                            }}
                        >
                            <MenuItem dir={direction} value="compact">{t('TableViewCompact')}</MenuItem>
                            <MenuItem dir={direction} value="standard">{t('TableViewStandard')}</MenuItem>
                            <MenuItem dir={direction} value="full">{t('TableViewFull')}</MenuItem>
                            <MenuItem dir={direction} value="custom">{t('TableViewCustom')}</MenuItem>
                        </Select>
                    </FormControl>
                    <Button
                        variant="outlined"
                                                                            size="small"
                        startIcon={<ViewColumnIcon />}
                        onClick={(e) => setColumnsPanelAnchor(e.currentTarget)}
                        className={documentGridStyles.columnsBtn}
                                                                            sx={{
                            minHeight: 'var(--xtram-control-height, 40px)',
                            height: 'var(--xtram-control-height, 40px)',
                            textTransform: 'none',
                            borderRadius: '10px',
                            borderColor: '#e5e7f6',
                            color: '#304FFF',
                            fontSize: 'var(--xtram-table-view-label, 11px)',
                            fontWeight: 600,
                            background: '#f5f6fd',
                            '&:hover': { borderColor: '#304FFF', background: '#eaedff' },
                        }}
                    >
                        {t('ShowHideColumns')}
                    </Button>
                                                                </div>
                {client?.managesProject && (
                    <label
                        className={`${documentGridStyles.byProjects}${byProjectsFilterActive ? ` ${documentGridStyles.checked}` : ''}`}
                    >
                        <Checkbox
                            checked={!!byProjectsFilterActive}
                            onChange={(event) => onByProjectsFilterToggle?.(event.target.checked)}
                            name="ByProjects"
                            size="small"
                            sx={{
                                padding: '2px',
                                color: '#c5c8e0',
                                '&.Mui-checked': { color: '#304fff' },
                            }}
                        />
                        <span>{t('ByProjects')}</span>
                    </label>
                )}
                {client?.managesProject && byProjectsFilterActive && (
                    <div className={documentGridStyles.projectPickers} dir={direction}>
                        {loadingSegmentations && <CircularProgress size={22} />}
                        <div className={documentGridStyles.projectField}>
                            <Autocomplete
                                                                            size="small"
                                loading={loadingSegmentations}
                                options={projectAutocompleteOptions}
                                getOptionLabel={(opt) => opt?.label ?? ''}
                                isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                                value={selectedFromSegmentation}
                                onChange={onFromSegmentationChange}
                                PopperComponent={(popperProps) => (
                                    <StyledProjectsPopper {...popperProps} dir={direction} />
                                )}
                                ListboxProps={{ dir: direction }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder={t('FromProject')}
                                        size="small"
                                        variant="standard"
                                        dir={direction}
                                        InputProps={{
                                            ...params.InputProps,
                                            disableUnderline: true,
                                        }}
                                    />
                                )}
                                clearOnBlur={false}
                                dir={direction}
                            />
                                                                </div>
                        <div className={documentGridStyles.projectField}>
                            <Autocomplete
                                size="small"
                                loading={loadingSegmentations}
                                options={projectAutocompleteOptions}
                                getOptionLabel={(opt) => opt?.label ?? ''}
                                isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                                value={selectedToSegmentation}
                                onChange={onToSegmentationChange}
                                PopperComponent={(popperProps) => (
                                    <StyledProjectsPopper {...popperProps} dir={direction} />
                                )}
                                ListboxProps={{ dir: direction }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder={t('ToProject')}
                                        size="small"
                                        variant="standard"
                                        dir={direction}
                                        InputProps={{
                                            ...params.InputProps,
                                            disableUnderline: true,
                                                                                }}
                                                                            />
                                                                        )}
                                clearOnBlur={false}
                                dir={direction}
                            />
                                                                    </div>
                    </div>
                )}
                <Popover
                    open={Boolean(columnsPanelAnchor)}
                    anchorEl={columnsPanelAnchor}
                    onClose={() => setColumnsPanelAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: direction === 'rtl' ? 'right' : 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: direction === 'rtl' ? 'right' : 'left' }}
                    PaperProps={{ sx: COLUMN_PANEL_PAPER_SX }}
                >
                    {visibleColumns
                        .filter((col) => col.field !== 'expand' && col.field !== 'actions')
                        .map((col, index, arr) => {
                            const isChecked = columnVisibilityModel[col.field] !== false;
                            return (
                                <FormControlLabel
                                    key={col.field}
                                    dir={direction}
                                    control={
                                        <Checkbox
                                                                            size="small"
                                            checked={isChecked}
                                            onChange={(e) => {
                                                setColumnVisibilityModel((prev) => ({
                                                    ...DEFAULT_HIDDEN_COLUMNS,
                                                    ...prev,
                                                    [col.field]: e.target.checked,
                                                }));
                                                setTableView((prev) => (prev === 'custom' ? prev : 'custom'));
                                                                            }}
                                                                            sx={{
                                                color: isChecked ? '#304FFF' : '#8b93a7',
                                                '&.Mui-checked': { color: '#304FFF' },
                                            }}
                                        />
                                    }
                                    label={col.headerName || col.field}
                                    sx={{
                                        display: 'flex',
                                        width: '100%',
                                        mx: 0,
                                        py: 0.65,
                                        minHeight: 'var(--xtram-menu-item-height)',
                                        height: 'auto',
                                        cursor: 'pointer',
                                        transition: 'background-color 0.2s ease, color 0.2s ease',
                                        borderBottom: index < arr.length - 1 ? '1px solid #f0f0f0' : 'none',
                                        backgroundColor: isChecked ? 'rgba(48, 79, 255, 0.1)' : 'transparent',
                                        '& .MuiFormControlLabel-label': {
                                            fontSize: 'var(--xtram-text-sm)',
                                            fontWeight: isChecked ? 500 : 400,
                                            lineHeight: 1.35,
                                            color: isChecked ? '#304FFF' : '#696665',
                                        },
                                        '&:hover': {
                                            backgroundColor: '#304fff',
                                            '& .MuiFormControlLabel-label': { color: '#ffffff' },
                                        },
                                        '&:hover .MuiCheckbox-root': { color: '#ffffff' },
                                        '&:hover .MuiCheckbox-root.Mui-checked': { color: '#ffffff' },
                                    }}
                                />
                            );
                        })}
                    <Box sx={{ px: 1.5, py: 0.75, display: 'flex', gap: 1, borderTop: '1px solid #f0f0f0' }}>
                        <Button
                                                                            size="small"
                            onClick={() => {
                                const allVisible = {};
                                visibleColumns.forEach((c) => {
                                    if (c.field !== 'expand' && c.field !== 'actions') {
                                        allVisible[c.field] = true;
                                    }
                                });
                                setColumnVisibilityModel((prev) => ({
                                    ...DEFAULT_HIDDEN_COLUMNS,
                                    ...prev,
                                    ...allVisible,
                                }));
                            }}
                            sx={{ textTransform: 'none', fontSize: 'var(--xtram-text-xs, 11px)', color: '#304FFF' }}
                        >
                            {t('ShowAll')}
                        </Button>
                        <Button
                            size="small"
                            onClick={() => {
                                const allHidden = {};
                                visibleColumns.forEach((c) => {
                                    if (c.field !== 'expand' && c.field !== 'actions') {
                                        allHidden[c.field] = false;
                                    }
                                });
                                setColumnVisibilityModel((prev) => ({
                                    ...DEFAULT_HIDDEN_COLUMNS,
                                    ...prev,
                                    ...allHidden,
                                }));
                            }}
                            sx={{ textTransform: 'none', fontSize: 'var(--xtram-text-xs, 11px)', color: '#696665' }}
                        >
                            {t('HideAll')}
                        </Button>
                                        </Box>
                </Popover>
                                    </Box>

            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getLocale()}>
                <StyledDataGrid
                    key={tableView}
                    rows={gridRows}
                    columns={visibleColumns}
                    getRowId={(row) => row.id}
                    checkboxSelection={isCheckboxSelectionEnabled}
                    disableRowSelectionOnClick
                    columnHeaderHeight={42}
                    rowSelectionModel={selectedRows || []}
                    onRowSelectionModelChange={handleSelectionModelChange}
                    isRowSelectable={isRowSelectable}
                    onCellDoubleClick={handleCellDoubleClick}
                    columnVisibilityModel={{ ...columnVisibilityModel, expand: true, actions: true }}
                    onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
                    components={{ Toolbar: GridToolbar }}
                    componentsProps={{
                        toolbar: {
                            showQuickFilter: true,
                        },
                    }}
                    pagination
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={[25, 50, 100]}
                    sortingMode="server"
                    sortModel={sortModel}
                    onSortModelChange={setSortModel}
                    sortingOrder={['asc', 'desc']}
                    getRowHeight={getRowHeight}
                    getEstimatedRowHeight={() => 120}
                    getRowClassName={getRowClassName}
                    sx={{ height: '48vh' }}
                />
            </LocalizationProvider>
                                                                    </div>
    );
};

export default PaymentAcceptanceTable;
